import { useNavigate } from 'react-router-dom';

import { mapPlanYearDates } from 'features/MyBenefits/utils';
import {
  BenefitDto,
  useBenefitsQuery,
  useConfigurationQuery,
  useEnrollmentContextQuery,
} from 'theSource/queries';

export const MyBenefitsNavItems = (lumosEnabled: boolean) => {
  const navigate = useNavigate();
  const { data: benefitsData, isLoading: isLoadingBenefits } = useBenefitsQuery(
    { enabled: true }
  );
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const { enrollmentContext } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });

  function validContexts(benefits: BenefitDto[]) {
    const dates = mapPlanYearDates(benefits);

    // don't show benefits if there is a NH or OE context in progress
    // if there are no contexts that means they have closed so cannot be open
    const hasAvailableContexts = enrollmentContext?.data.filter((enrollment) =>
      dates.opyIds?.has(enrollment.org_plan_year_id)
    );

    if (!hasAvailableContexts || hasAvailableContexts.length === 0) {
      return {
        associatedContexts: true,
        startsOn: dates.startsOn,
        endsOn: dates.endsOn,
      };
    }

    const hasInProgressContexts = hasAvailableContexts?.filter(
      (enrollment) =>
        enrollment.enrollment_type_lookup_code === 'life_event' ||
        !['started', 'not_started'].includes(enrollment.status)
    );

    return {
      associatedContexts: Boolean(
        hasInProgressContexts && hasInProgressContexts.length > 0
      ),
      startsOn: dates.startsOn,
      endsOn: dates.endsOn,
    };
  }

  let benefitsNavRoutes: {
    [key: string]: {
      name: string;
      path: string;
      handleClick: () => void;
    };
  } = {};

  if (isLoadingBenefits) {
    return {};
  }

  if (lumosEnabled && benefitsData) {
    if (benefitsData.data.current.length !== 0) {
      const context = validContexts(benefitsData.data.current);

      if (context.associatedContexts) {
        benefitsNavRoutes = {
          myCurrentBenefits: {
            name: 'navigation.additional:benefits.myBenefitsCurrentLinkTitle',
            path: '/benefits?context=current',
            handleClick: () => {
              navigate('/benefits?context=current', {
                state: {
                  state: {
                    context: 'current',
                    startDate: context.startsOn,
                    endDate: context.endsOn,
                  },
                },
              });
            },
          },
        };
      }
    }
    if (benefitsData?.data.next.length !== 0) {
      const context = validContexts(benefitsData.data.next);

      if (context.associatedContexts) {
        benefitsNavRoutes = {
          ...benefitsNavRoutes,
          myFutureBenefits: {
            name: 'navigation.additional:benefits.myBenefitsFutureLinkTitle',
            path: '/benefits?context=future',
            handleClick: () => {
              navigate('/benefits?context=future', {
                state: {
                  state: {
                    context: 'future',
                    startDate: context.startsOn,
                    endDate: context.endsOn,
                  },
                },
              });
            },
          },
        };
      }
    }
    if (benefitsData?.data.previous.length !== 0) {
      benefitsNavRoutes = {
        ...benefitsNavRoutes,
        myPastBenefits: {
          name: 'navigation.additional:benefits.myBenefitsPastLinkTitle',
          path: '/benefits?context=past',
          handleClick: () => {
            navigate('/benefits?context=past', {
              state: { state: { context: 'past' } },
            });
          },
        },
      };
    }
  } else {
    benefitsNavRoutes = {
      myCurrentBenefits: {
        name: 'navigation.additional:benefits.myBenefitsLinkTitle',
        path: '/benefits?context=current',
        handleClick: () => {
          navigate('/benefits?context=current', {
            state: { state: { context: 'current' } },
          });
        },
      },
    };
  }

  if (Object.keys(benefitsNavRoutes).length > 0) {
    return { benefits: benefitsNavRoutes };
  }
};
